<template>
  <div v-if="over" class="countdown countdown--over">
    <p class="mb-0">{{ overText }}</p>
  </div>
  <div
    v-else
    class="countdown flex-wrap"
    :class="{ 'countdown--soon': numberOf.days === 0 }"
  >
    <div v-if="!skeleton && beforeText" class="countdown__before pl-0-4 pr-0-4">
      {{ beforeText }}
    </div>
    <div
      v-if="skeleton"
      class="countdown__inner d-flex justify-center align-center"
    >
      <strong class="skeleton skeleton--bar" style="width: 120px"
        >&nbsp;</strong
      >
    </div>
    <div v-else class="countdown__inner d-flex justify-center align-center">
      <span v-if="numberOf.days > 1"
        >{{ numberOf.days }} {{ $tc('countdown.days', numberOf.days) }}</span
      >
      <span v-if="numberOf.days <= 1 && numberOf.hours > 0">
        <transition @enter="enter" @leave="leave">
          <span v-if="numberOf.hours < 10 && !showUnit" :key="0" class="left"
            >0</span
          >
          <span
            v-else-if="numberOf.hours >= 10"
            :key="numberOf.hours.toString().substr(0, 1)"
            class="left"
            >{{ numberOf.hours.toString().substr(0, 1) }}</span
          >
        </transition>
        <transition @enter="enter" @leave="leave">
          <span
            v-if="numberOf.hours < 10"
            :key="numberOf.hours.toString().substr(0, 1)"
            class="right"
            >{{ numberOf.hours.toString().substr(0, 1) }}</span
          >
          <span
            v-else
            :key="numberOf.hours.toString().substr(1, 2)"
            class="right"
            >{{ numberOf.hours.toString().substr(1, 2) }}</span
          >
        </transition>
      </span>

      <span
        v-if="numberOf.days <= 1 && numberOf.hours > 0"
        class="countdown__separator"
      >
        {{ showUnit || numberOf.days > 0 ? $tc('hours', numberOf.hours) : ':' }}

        <span v-if="showUnit && numberOf.days === 0 && numberOf.hours > 0"
          >&nbsp;{{ $t('and') }}</span
        >
      </span>

      <span v-if="numberOf.days === 0">
        <transition @enter="enter" @leave="leave">
          <span v-if="numberOf.minutes < 10 && !showUnit" :key="0" class="left"
            >0</span
          >
          <span
            v-else-if="numberOf.minutes >= 10"
            :key="numberOf.minutes.toString().substr(0, 1)"
            class="left"
            >{{ numberOf.minutes.toString().substr(0, 1) }}</span
          >
        </transition>
        <transition @enter="enter" @leave="leave">
          <span
            v-if="numberOf.minutes < 10"
            :key="numberOf.minutes.toString().substr(0, 1)"
            class="right"
            >{{ numberOf.minutes.toString().substr(0, 1) }}</span
          >
          <span
            v-else
            :key="numberOf.minutes.toString().substr(1, 2)"
            class="right"
            >{{ numberOf.minutes.toString().substr(1, 2) }}</span
          >
        </transition>
      </span>

      <span v-if="numberOf.days === 0" class="countdown__separator"
        >{{
          showUnit ? $tc('minutes', numberOf.minutes) : showSeconds ? ':' : ''
        }}
        <span v-if="showUnit && numberOf.days === 0 && numberOf.hours === 0"
          >&nbsp;{{ $t('and') }}</span
        >
      </span>

      <span v-if="showSeconds">
        <transition @enter="enter" @leave="leave">
          <span v-if="numberOf.seconds < 10" :key="0" class="left">0</span>
          <span
            v-else
            :key="numberOf.seconds.toString().substr(0, 1)"
            class="left"
            >{{ numberOf.seconds.toString().substr(0, 1) }}</span
          >
        </transition>
        <transition @enter="enter" @leave="leave">
          <span
            v-if="numberOf.seconds < 10"
            :key="numberOf.seconds.toString().substr(0, 1)"
            class="right"
            >{{ numberOf.seconds.toString().substr(0, 1) }}</span
          >
          <span
            v-else
            :key="numberOf.seconds.toString().substr(1, 2)"
            class="right"
            >{{ numberOf.seconds.toString().substr(1, 2) }}</span
          >
        </transition>
      </span>

      <span
        v-if="showUnit && numberOf.days === 0 && numberOf.hours === 0"
        class="countdown__separator"
        >{{ $tc('seconds', numberOf.seconds) }}</span
      >
    </div>
    <slot />
  </div>
</template>

<script>
const one = {
  day: 1000 * 60 * 60 * 24,
  hour: 1000 * 60 * 60,
  minute: 1000 * 60,
  second: 1000,
}

export default {
  name: 'CountDown',
  props: {
    end: {
      type: Date,
      required: true,
    },
    overText: {
      type: String,
      required: true,
    },
    beforeText: {
      type: String,
      default: '',
    },
    showUnit: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    numberOf: {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
    over: false,
    interval: null,
  }),
  computed: {
    showSeconds() {
      if (this.numberOf.days === 0) {
        if (this.showUnit) {
          if (this.numberOf.hours === 0) {
            return true
          }
        } else {
          return true
        }
      }
      return false
    },
  },
  mounted() {
    this.countdown()

    this.interval = setInterval(() => {
      this.countdown()
    }, 1000)

    this.$emit('mounted')
  },
  methods: {
    countdown() {
      const now = new Date()

      const endTime = this.end.getTime()
      const nowTime = now.getTime()

      if (endTime > nowTime) {
        const timeDuration = endTime - nowTime

        this.numberOf = {
          days: Math.floor(timeDuration / one.day),
          hours: Math.floor(timeDuration / one.hour),
          minutes: Math.floor((timeDuration % one.hour) / one.minute),
          seconds: Math.floor((timeDuration % one.minute) / one.second),
        }

        return
      }

      this.over = true
      clearInterval(this.interval)
    },
    async leave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
      gsap.to(el, {
        bottom: '100%',
        opacity: 0,
        duration: 0.5,
        onComplete: done,
      })
    },
    async enter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.from(el, {
        bottom: '-100%',
        opacity: 0,
        duration: 0.5,
        onComplete: done,
      })
    },
  },
}
</script>

<style lang="scss">
.countdown {
  display: flex;
  align-items: center;
  justify-content: center;

  &__inner {
    p {
      padding: 0;
    }

    span {
      position: relative;
      display: flex;

      &.v-leave-active {
        position: absolute;
      }
    }
  }

  &__separator {
    margin: 0 rem(5px);
  }

  .left {
    left: 0;
  }

  .right {
    right: 0;
  }
}
</style>
